import { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import './lecke.css';

/* ══════════════════════════════════════════════════════════════════════════
   ELLENŐRZŐ KÉRDÉSEK a lecke alján (Tananyag 1. kör) — a leckekvíz (quizzes.lesson_id)
   ─────────────────────────────────────────────────────────────────────────
   Kérdésenként azonnali visszajelzés a magyarázattal; a végén „3/3”; újrapróbálható.
   Az utolsó válasz után a kísérlet mentődik (quiz_attempts), de semmit nem zár le:
   se a lecke teljesítése, se más lecke nem függ tőle. A modulkvíz marad a kurzusoldalon.
   A kvízt és a kérdéseket az RLS adja: csak az látja, aki a leckét is.
   Az `api` a tesztekhez cserélhető.
   ══════════════════════════════════════════════════════════════════════════ */

const LABELS = ['A', 'B', 'C', 'D'];

export const supabaseApi = {
  async kviz(lessonId) {
    const { data, error } = await supabase.from('quizzes')
      .select('id, title, pass_ratio, quiz_questions(id, order_index, question, options, correct_index, explanation)')
      .eq('lesson_id', lessonId).maybeSingle();
    if (error) throw error;
    return data;
  },
  async ment(row) {
    const { error } = await supabase.from('quiz_attempts').insert(row);
    if (error) throw error;
  },
};

/** A gomb állapota: válasz előtt üres; utána a helyes 'ok', a rossz választás 'bad', a többi 'dim'. */
export function opcioAllapot(valasztott, helyes, k) {
  if (valasztott == null) return '';
  if (k === helyes) return 'ok';
  return k === valasztott ? 'bad' : 'dim';
}

export default function LessonCheck({ lessonId, userId, api = supabaseApi }) {
  const [kviz, setKviz] = useState(null);
  const [valaszok, setValaszok] = useState({});
  const [hiba, setHiba] = useState('');
  const [mentve, setMentve] = useState(false);

  useEffect(() => {
    let elo = true;
    setKviz(null); setValaszok({}); setMentve(false); setHiba('');
    api.kviz(lessonId).then(k => { if (elo) setKviz(k); }, () => { if (elo) setKviz(null); });
    return () => { elo = false; };
  }, [api, lessonId]);

  const kerdesek = [...(kviz?.quiz_questions || [])].sort((a, b) => a.order_index - b.order_index);
  const kesz = kerdesek.length > 0 && kerdesek.every(q => valaszok[q.id] != null);
  const pont = kerdesek.filter(q => valaszok[q.id] === q.correct_index).length;

  useEffect(() => {
    if (!kesz || mentve || !userId) return;
    setMentve(true);
    const ratio = Number(kviz.pass_ratio) || 0.7;
    api.ment({ user_id: userId, quiz_id: kviz.id, score: pont, total: kerdesek.length, passed: pont >= Math.ceil(kerdesek.length * ratio) })
      .catch(() => setHiba('Az eredményt most nem sikerült menteni; a válaszaid itt megmaradtak.'));
  }, [kesz, mentve, userId, kviz, pont, kerdesek.length, api]);

  if (!kerdesek.length) return null;
  const ujra = () => { setValaszok({}); setMentve(false); setHiba(''); };

  return (
    <section className="lck" aria-label="Ellenőrző kérdések">
      <h2>Ellenőrző kérdések</h2>
      <p className="lck-sub">{kerdesek.length} rövid kérdés a leckéhez. Nem számít bele semmibe, csak neked szól.</p>
      {kerdesek.map((q, i) => {
        const v = valaszok[q.id];
        return (
          <div className="lck-q" key={q.id}>
            <p>{i + 1}. {q.question}</p>
            {q.options.map((o, k) => (
              <button key={k} type="button" className={`lck-opt ${opcioAllapot(v, q.correct_index, k)}`} disabled={v != null}
                onClick={() => setValaszok(a => ({ ...a, [q.id]: k }))}>
                <b>{LABELS[k]}</b><span>{o}</span>
              </button>
            ))}
            {v != null && (
              <p className="lck-fb" role="status">
                <strong className={v === q.correct_index ? 'ok' : 'bad'}>{v === q.correct_index ? 'Helyes. ' : 'Nem egészen. '}</strong>
                {q.explanation}
              </p>
            )}
          </div>
        );
      })}
      {kesz && (
        <div className="lck-end">
          <span className="lck-score" aria-label={`${pont} helyes ${kerdesek.length} kérdésből`}>{pont}/{kerdesek.length}</span>
          <button type="button" className="lck-btn" onClick={ujra}>Újra</button>
        </div>
      )}
      {hiba && <p className="lck-err" role="alert">{hiba}</p>}
    </section>
  );
}
